import { useEffect, useState } from 'react';
import { PERMISSIONS } from '@hotspot/shared';
import { ApiRequestError, apiList, apiRequest } from '../lib/api';
import { useAuth } from '../lib/auth';
import type { UnitDto } from '../lib/types';
import { Alert, Button, Card, Field, Spinner } from '../components/ui';

export function UnitsPage() {
  const { can } = useAuth();
  const podeEscrever = can(PERMISSIONS.UNIT_WRITE);

  const [unidades, setUnidades] = useState<UnitDto[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', city: '', state: '', address: '' });
  const [formError, setFormError] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  async function carregar() {
    setErro(null);
    try {
      const { data } = await apiList<UnitDto>('/admin/units');
      setUnidades(data);
    } catch (caught) {
      setUnidades([]);
      setErro(caught instanceof ApiRequestError ? caught.displayMessage : 'Falha ao carregar');
    }
  }

  useEffect(() => {
    void carregar();
  }, []);

  async function criar(event: React.FormEvent) {
    event.preventDefault();
    setFormError(null);
    setSalvando(true);
    try {
      await apiRequest('/admin/units', {
        method: 'POST',
        body: {
          name: form.name,
          city: form.city || null,
          state: form.state || null,
          address: form.address || null,
        },
      });
      setForm({ name: '', city: '', state: '', address: '' });
      await carregar();
    } catch (caught) {
      setFormError(caught instanceof ApiRequestError ? caught.displayMessage : 'Falha ao criar');
    } finally {
      setSalvando(false);
    }
  }

  async function remover(unidade: UnitDto) {
    if (!window.confirm(`Remover a unidade "${unidade.name}"?`)) return;
    try {
      await apiRequest(`/admin/units/${unidade.id}`, { method: 'DELETE' });
      await carregar();
    } catch (caught) {
      setErro(caught instanceof ApiRequestError ? caught.displayMessage : 'Falha ao remover');
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Unidades</h1>
        <p className="text-sm text-slate-500">
          Locais aos quais os equipamentos e as campanhas sao vinculados.
        </p>
      </div>

      <Card title="Unidades cadastradas">
        {erro ? <Alert>{erro}</Alert> : null}

        {unidades === null ? (
          <Spinner />
        ) : unidades.length === 0 ? (
          <p className="text-sm text-slate-500">Nenhuma unidade cadastrada.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-xs tracking-wide text-slate-500 uppercase">
                  <th className="py-2 pr-4">Nome</th>
                  <th className="py-2 pr-4">Identificador</th>
                  <th className="py-2 pr-4">Cidade/UF</th>
                  <th className="py-2 pr-4">Equipamentos</th>
                  {podeEscrever ? <th className="py-2" /> : null}
                </tr>
              </thead>
              <tbody>
                {unidades.map((unidade) => (
                  <tr key={unidade.id} className="border-b border-slate-100 last:border-0">
                    <td className="py-2 pr-4 font-medium text-slate-800">{unidade.name}</td>
                    <td className="py-2 pr-4 font-mono text-xs text-slate-500">{unidade.slug}</td>
                    <td className="py-2 pr-4 text-slate-600">
                      {unidade.city ? `${unidade.city}${unidade.state ? `/${unidade.state}` : ''}` : '—'}
                    </td>
                    <td className="py-2 pr-4 text-slate-600 tabular-nums">
                      {unidade.mikrotikCount}
                    </td>
                    {podeEscrever ? (
                      <td className="py-2 text-right">
                        <Button variant="ghost" onClick={() => void remover(unidade)}>
                          Remover
                        </Button>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {podeEscrever ? (
        <Card title="Nova unidade">
          <form onSubmit={criar} className="grid gap-4 sm:grid-cols-2">
            {formError ? (
              <div className="sm:col-span-2">
                <Alert>{formError}</Alert>
              </div>
            ) : null}

            <Field
              label="Nome"
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
              required
              minLength={2}
              hint="O identificador usado nas URLs e gerado a partir do nome."
            />
            <Field
              label="Cidade"
              value={form.city}
              onChange={(event) => setForm({ ...form, city: event.target.value })}
            />
            <Field
              label="UF"
              value={form.state}
              onChange={(event) => setForm({ ...form, state: event.target.value })}
              maxLength={2}
            />
            <Field
              label="Endereco"
              value={form.address}
              onChange={(event) => setForm({ ...form, address: event.target.value })}
            />

            <div className="sm:col-span-2">
              <Button type="submit" disabled={salvando}>
                {salvando ? 'Salvando...' : 'Criar unidade'}
              </Button>
            </div>
          </form>
        </Card>
      ) : null}
    </div>
  );
}
